import { apiConfig } from '@nomiss/configs';
import { API_ENDPOINTS, DEVICE_ID_KEY } from '@nomiss/constants';
import {
  Configuration,
  type Middleware,
  NotificationsApi,
  AuthApi,
  OtpApi,
  WaitlistApi,
  AccountDeletionRequestsApi,
  OnboardingApi,
  PlayersApi,
  TeamsApi,
  UsersApi,
  SpoilerShieldApi,
  FAQsApi,
  LegalApi,
  MediaApi,
  ReferralsApi,
  AdminApi,
  AdminUsersApi,
  AdminContentApi,
  AdminMonitoringApi,
  AdminSubscriptionsApi,
  AdminReferralsApi,
  AdminStreamingPlatformsApi,
  AdminSportTopPlayersApi,
  AdminHighlightSourcesApi,
  AdminAlertsApi,
  DigestApi,
  SubscriptionsApi,
} from '@nomiss/sdk';
import {
  ADMIN_ACCESS_TOKEN_KEY,
  ADMIN_REFRESH_TOKEN_KEY,
} from './admin-session-keys';
import { commaSeparatedQuerystring } from './comma-separated-querystring';
import { HttpStatus } from './http-status';
import {
  resolveMobileLogoutReason,
  type MobileLogoutReason,
} from './auth-session-errors';

const isReactNative =
  typeof navigator !== 'undefined' &&
  typeof (navigator as Navigator).product === 'string' &&
  (navigator as Navigator).product === 'ReactNative';

type TokenRefreshHandler = (
  accessToken: string,
  refreshToken: string,
) => void | Promise<void>;

type AccessTokenResolver = () => string | null;
type LanguageResolver = () => string | null;

/**
 * Module-level auth state is not enough: Metro can evaluate this file twice,
 * and a request handled by the copy that never received `setSdkTokens` would
 * 401 with no refresh token to recover. One binding on globalThis keeps the
 * live session, resolvers, and refresh callback shared.
 */
type SdkAuthBindings = {
  accessToken: string | null;
  refreshToken: string | null;
  deviceId: string | null;
  deviceFingerprint: string | null;
  accessTokenResolver: AccessTokenResolver | null;
  languageResolver: LanguageResolver | null;
  actionOnTokenRefresh: TokenRefreshHandler | null;
  actionOnMobileLogout: (reason?: MobileLogoutReason) => void;
};

const SDK_AUTH_BINDINGS_KEY = '__nomissSdkAuthBindings';

function sdkAuthBindings(): SdkAuthBindings {
  const root = globalThis as typeof globalThis & {
    [SDK_AUTH_BINDINGS_KEY]?: SdkAuthBindings;
  };
  const existing = root[SDK_AUTH_BINDINGS_KEY];
  if (existing) return existing;

  const created: SdkAuthBindings = {
    accessToken: null,
    refreshToken: null,
    deviceId: null,
    deviceFingerprint: null,
    accessTokenResolver: null,
    languageResolver: null,
    actionOnTokenRefresh: null,
    actionOnMobileLogout: () => {},
  };
  root[SDK_AUTH_BINDINGS_KEY] = created;
  return created;
}

export const setActionOnMobileLogout = (
  action: (reason?: MobileLogoutReason) => void,
) => {
  sdkAuthBindings().actionOnMobileLogout = action;
};

export const setActionOnTokenRefresh = (action: TokenRefreshHandler) => {
  sdkAuthBindings().actionOnTokenRefresh = action;
};

type SharedRefreshState = {
  endedSession: boolean;
  inflight: Promise<TokenRefreshOutcome> | null;
};

const SHARED_REFRESH_STATE_KEY = '__nomissSharedRefreshState';

/**
 * Kept on globalThis so the admin UI and the API client share one refresh,
 * even when the bundler loads this module twice.
 */
function sharedRefreshState(): SharedRefreshState {
  const root = globalThis as typeof globalThis & {
    [SHARED_REFRESH_STATE_KEY]?: SharedRefreshState;
  };
  const existing = root[SHARED_REFRESH_STATE_KEY];
  if (existing) return existing;

  const created: SharedRefreshState = {
    endedSession: false,
    inflight: null,
  };
  root[SHARED_REFRESH_STATE_KEY] = created;
  return created;
}

export function didRefreshEndSession(): boolean {
  return sharedRefreshState().endedSession;
}

export function clearRefreshEndedSession(): void {
  sharedRefreshState().endedSession = false;
}

function readSessionStorage(): Storage | null {
  const storage = (globalThis as { sessionStorage?: Storage }).sessionStorage;
  if (!storage || typeof storage.getItem !== 'function') return null;
  return storage;
}

function readStoredAdminRefreshToken(): string | null {
  const token = readSessionStorage()?.getItem(ADMIN_REFRESH_TOKEN_KEY);
  if (!token?.trim()) return null;
  return token.trim();
}

export const setAccessTokenResolver = (
  resolver: AccessTokenResolver | null,
) => {
  sdkAuthBindings().accessTokenResolver = resolver;
};

export const setLanguageResolver = (resolver: LanguageResolver | null) => {
  sdkAuthBindings().languageResolver = resolver;
};

export const resolveLanguageCode = (): string | null => {
  const resolved = sdkAuthBindings().languageResolver?.() ?? null;
  if (resolved && resolved.trim() !== '') {
    return resolved.trim();
  }
  return null;
};

export const getLanguageHeaders = (): Record<string, string> => {
  const languageCode = resolveLanguageCode();
  if (!languageCode) {
    return {};
  }

  return {
    'Accept-Language': languageCode,
    'X-Language': languageCode,
  };
};

export function buildApiHeaders(
  init?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = {
    ...apiConfig.defaultHeaders,
    ...getLanguageHeaders(),
    ...(isReactNative ? { 'x-device': 'mobile' } : {}),
    ...init,
  };

  // Raw fetch callers (admin data-sync/settings/broadcast) rely on
  // this helper. SDK requests get Bearer via middleware separately; without this,
  // fetch-based admin routes hit JwtAuthGuard as Unauthorized.
  if (!('Authorization' in headers) && !('authorization' in headers)) {
    const token = resolveAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
}

export const getSdkAccessToken = (): string | null =>
  sdkAuthBindings().accessToken;

/**
 * Prefer whichever access token expires later. The auth store can lag the SDK
 * cache while a refresh token is still being written to the keychain, and a
 * caller may also have closed over the previous access token.
 */
export const resolveAccessToken = (): string | null => {
  const bindings = sdkAuthBindings();
  return pickFresherAccessToken(
    bindings.accessTokenResolver?.() ?? null,
    bindings.accessToken,
  );
};

export const setSdkTokens = (
  accessToken: string | null,
  refreshToken: string | null,
) => {
  const bindings = sdkAuthBindings();
  bindings.accessToken = accessToken;
  bindings.refreshToken = refreshToken;
};

/** Drops admin tokens after refresh reports the session is expired, superseded, or blocked. */
export function markSessionEndedByRefresh(): void {
  sharedRefreshState().endedSession = true;
  setSdkTokens(null, null);
  const storage = readSessionStorage();
  storage?.removeItem(ADMIN_ACCESS_TOKEN_KEY);
  storage?.removeItem(ADMIN_REFRESH_TOKEN_KEY);
}

export const setSdkDeviceId = (deviceId: string | null) => {
  sdkAuthBindings().deviceId = deviceId;
};

export const setSdkDeviceFingerprint = (deviceFingerprint: string | null) => {
  sdkAuthBindings().deviceFingerprint = deviceFingerprint;
};

/** Refresh this long before JWT `exp` so in-flight navigation does not 401. */
const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000;

function decodeBase64Url(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + '='.repeat(padLength);
  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(padded);
  }
  return Buffer.from(padded, 'base64').toString('utf8');
}

export function readJwtExpiryMs(token: string): number | null {
  const segment = token.split('.')[1];
  if (!segment) return null;

  try {
    const payload = JSON.parse(decodeBase64Url(segment)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function pickFresherAccessToken(
  primary: string | null | undefined,
  secondary: string | null | undefined,
): string | null {
  const first = primary?.trim() ? primary.trim() : null;
  const second = secondary?.trim() ? secondary.trim() : null;
  if (!first) return second;
  if (!second || first === second) return first;

  const firstExpiry = readJwtExpiryMs(first);
  const secondExpiry = readJwtExpiryMs(second);
  if (firstExpiry == null) return secondExpiry == null ? first : second;
  if (secondExpiry == null) return first;
  return secondExpiry > firstExpiry ? second : first;
}

function accessTokenNeedsRefresh(token: string | null): boolean {
  if (!token) return false;
  const expiresAt = readJwtExpiryMs(token);
  if (expiresAt == null) return false;
  return expiresAt - Date.now() <= ACCESS_TOKEN_REFRESH_SKEW_MS;
}

function readBearerToken(headers: Headers): string | null {
  const value = headers.get('Authorization');
  if (!value) return null;
  const match = /^Bearer\s+(.+)$/i.exec(value.trim());
  const token = match?.[1]?.trim();
  return token ? token : null;
}

function currentRefreshToken(): string | null {
  return sdkAuthBindings().refreshToken ?? readStoredAdminRefreshToken();
}

export type TokenRefreshOutcome =
  | {
      status: 'valid';
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }
  | { status: 'expired' }
  | { status: 'superseded' }
  | { status: 'blocked' }
  | { status: 'error'; message: string };

// A single in-flight refresh shared across the whole app. Because the backend
// rotates refresh tokens (the presented token is revoked as soon as it is used),
// two concurrent refreshes with the same token would make the second one look
// like a superseded/expired session and log the user out. De-duping guarantees
// only one network refresh runs at a time and every caller observes its result.
async function executeTokenRefresh(
  refreshToken: string,
  deviceFingerprint?: string,
): Promise<TokenRefreshOutcome> {
  try {
    const response = await fetch(
      `${apiConfig.baseUrl}${API_ENDPOINTS.AUTH_REFRESH}`,
      {
        method: 'POST',
        headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
        credentials: isReactNative ? 'include' : 'omit',
        body: JSON.stringify({
          refreshToken,
          ...(deviceFingerprint?.trim()
            ? { deviceFingerprint: deviceFingerprint.trim() }
            : {}),
        }),
      },
    );

    if (response.status === HttpStatus.UNAUTHORIZED) {
      const reason = await resolveMobileLogoutReason(response);
      return { status: reason };
    }

    if (!response.ok) {
      return {
        status: 'error',
        message: `Refresh failed with status ${response.status}`,
      };
    }

    const payload = (await response.json()) as {
      data?: {
        accessToken?: string;
        refreshToken?: string;
        expiresIn?: number;
      };
    };

    const accessToken = payload.data?.accessToken;
    const newRefreshToken = payload.data?.refreshToken;
    const expiresIn = payload.data?.expiresIn ?? 900;

    if (!accessToken || !newRefreshToken) {
      return { status: 'error', message: 'Invalid refresh response' };
    }

    setSdkTokens(accessToken, newRefreshToken);
    sharedRefreshState().endedSession = false;

    return {
      status: 'valid',
      accessToken,
      refreshToken: newRefreshToken,
      expiresIn,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Refresh failed',
    };
  }
}

/**
 * Refreshes the auth tokens using the backend rotation endpoint, ensuring that
 * concurrent callers share a single network request. On success the SDK's
 * in-memory tokens are updated; callers are responsible for durable persistence.
 */
export function performTokenRefresh(
  refreshToken: string,
  deviceFingerprint?: string,
): Promise<TokenRefreshOutcome> {
  const state = sharedRefreshState();
  if (state.inflight) {
    return state.inflight;
  }

  const request = executeTokenRefresh(refreshToken, deviceFingerprint).finally(
    () => {
      if (sharedRefreshState().inflight === request) {
        sharedRefreshState().inflight = null;
      }
    },
  );
  state.inflight = request;

  return request;
}

type FetchApi = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

type UnauthorizedSettlement =
  | { type: 'retry'; accessToken: string }
  | { type: 'passthrough' };

async function persistValidRefresh(
  outcome: Extract<TokenRefreshOutcome, { status: 'valid' }>,
): Promise<void> {
  sharedRefreshState().endedSession = false;
  const persist = sdkAuthBindings().actionOnTokenRefresh;
  if (persist) {
    await persist(outcome.accessToken, outcome.refreshToken);
  }
}

async function endSessionAfterFailedRefresh(
  reason: MobileLogoutReason,
): Promise<void> {
  if (sharedRefreshState().endedSession) {
    return;
  }

  markSessionEndedByRefresh();
  const deviceId = sdkAuthBindings().deviceId;
  if (deviceId) {
    try {
      await fetch(`${apiConfig.baseUrl}${API_ENDPOINTS.AUTH_LOGOUT}`, {
        method: 'POST',
        headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
        credentials: isReactNative ? 'include' : 'omit',
        body: JSON.stringify({ deviceId }),
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  setSdkDeviceId(null);
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    localStorage.removeItem(DEVICE_ID_KEY);
  }

  if (isReactNative) {
    sdkAuthBindings().actionOnMobileLogout(reason);
  }
}

/**
 * Refreshes once when the access token is expired or inside the skew window.
 * Concurrent callers share `performTokenRefresh`, so a burst of navigation
 * requests rotates the refresh token a single time.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const state = sharedRefreshState();
  if (state.inflight) {
    const outcome = await state.inflight;
    if (outcome.status === 'valid') {
      return outcome.accessToken;
    }
    return resolveAccessToken();
  }

  const refreshToken = currentRefreshToken();
  const accessToken = resolveAccessToken();
  if (!refreshToken || !accessTokenNeedsRefresh(accessToken)) {
    return accessToken;
  }

  const outcome = await performTokenRefresh(
    refreshToken,
    sdkAuthBindings().deviceFingerprint ?? undefined,
  );

  if (outcome.status === 'valid') {
    await persistValidRefresh(outcome);
    return outcome.accessToken;
  }

  if (outcome.status === 'error') {
    return accessToken;
  }

  await endSessionAfterFailedRefresh(outcome.status);
  return null;
}

async function applyFreshAuthorization(
  init?: RequestInit,
): Promise<RequestInit> {
  const headers = new Headers(init?.headers);
  const headerToken = readBearerToken(headers);
  const candidate = pickFresherAccessToken(headerToken, resolveAccessToken());
  const live = accessTokenNeedsRefresh(candidate)
    ? ((await ensureFreshAccessToken()) ?? candidate)
    : candidate;
  const token = pickFresherAccessToken(headerToken, live);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    ...init,
    headers,
  };
}

/**
 * One refresh attempt for a 401. A terminal failure clears the admin session
 * so the admin panel can log out. A transport failure leaves the session as-is.
 */
async function settleUnauthorized(): Promise<UnauthorizedSettlement> {
  const refreshToken = currentRefreshToken();
  if (!refreshToken) {
    // The refresh token is not loaded yet (e.g. during cold-start bootstrap)
    // or the user is already logged out. Session bootstrap owns the decision
    // to send the user to the auth flow, so do not force a logout here.
    return { type: 'passthrough' };
  }

  const outcome = await performTokenRefresh(
    refreshToken,
    sdkAuthBindings().deviceFingerprint ?? undefined,
  );

  if (outcome.status === 'valid') {
    await persistValidRefresh(outcome);
    return { type: 'retry', accessToken: outcome.accessToken };
  }

  if (outcome.status === 'error') {
    // Transient failure (e.g. network error / server hiccup). Keep the
    // session intact and let this request fail; a later request can retry.
    return { type: 'passthrough' };
  }

  await endSessionAfterFailedRefresh(outcome.status);
  return { type: 'passthrough' };
}

/**
 * fetch() for routes that do not go through the SDK middleware.
 * Sends a fresh access token when the current one is expired, then on 401
 * refreshes once and retries. A dead session is cleared before the 401 is
 * returned to the caller.
 */
export async function fetchWithAuthRecovery(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const prepared = await applyFreshAuthorization(init);
  const response = await fetch(input, prepared);
  if (response.status !== HttpStatus.UNAUTHORIZED) {
    return response;
  }

  const settlement = await settleUnauthorized();
  if (settlement.type !== 'retry') {
    return response;
  }

  const headers = new Headers(prepared.headers);
  headers.set('Authorization', `Bearer ${settlement.accessToken}`);
  return fetch(input, { ...prepared, headers });
}

const middleware: Middleware = {
  async post({ url, init, response }) {
    if (response.status !== HttpStatus.UNAUTHORIZED) {
      return;
    }

    const settlement = await settleUnauthorized();
    if (settlement.type !== 'retry') {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);
    headers.set('Authorization', `Bearer ${settlement.accessToken}`);

    // Retry with the raw fetch so we do not re-enter this middleware and do
    // not add custom headers (e.g. x-refresh-retry). Those headers are not
    // on the API CORS allowlist, so the browser would block the retry —
    // the first admin action after token expiry would fail, the next succeed.
    return fetchApi(url, {
      ...init,
      headers,
    });
  },
};

const fetchApi: FetchApi = (input, init) => fetch(input, init);

const languageMiddleware: Middleware = {
  async pre({ url, init }) {
    const headers = new Headers(init.headers as HeadersInit | undefined);
    const languageHeaders = getLanguageHeaders();
    let hasLanguageHeader = false;

    for (const [key, value] of Object.entries(languageHeaders)) {
      if (!headers.get(key)) {
        headers.set(key, value);
        hasLanguageHeader = true;
      }
    }

    if (!hasLanguageHeader) {
      return;
    }

    return {
      url,
      init: {
        ...init,
        headers,
      },
    };
  },
};

/**
 * OpenAPI `createFetchParams` shallow-merges initOverrides over the request.
 * When a caller passes `{ headers: { Authorization } }`, the whole headers
 * object is replaced and `Content-Type: application/json` is dropped, so Nest
 * never parses the JSON body. Restore the content type here for any request
 * that carries a serialized body.
 */
const jsonContentTypeMiddleware: Middleware = {
  async pre({ url, init }) {
    if (typeof init.body !== 'string') {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);

    if (headers.get('Content-Type')) {
      return;
    }

    headers.set('Content-Type', 'application/json');

    return {
      url,
      init: {
        ...init,
        headers,
      },
    };
  },
};

const authMiddleware: Middleware = {
  async pre({ url, init }) {
    if (String(url).includes(API_ENDPOINTS.AUTH_REFRESH)) {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);
    const headerToken = readBearerToken(headers);
    const candidate = pickFresherAccessToken(
      headerToken,
      resolveAccessToken(),
    );
    const live = accessTokenNeedsRefresh(candidate)
      ? ((await ensureFreshAccessToken()) ?? candidate)
      : candidate;
    const token = pickFresherAccessToken(headerToken, live);

    if (!token) {
      return;
    }

    if (headers.get('Authorization') === `Bearer ${token}`) {
      return;
    }

    headers.set('Authorization', `Bearer ${token}`);

    return {
      url,
      init: {
        ...init,
        headers,
      },
    };
  },
};

const sdkConfiguration = new Configuration({
  basePath: apiConfig.baseUrl,
  fetchApi,
  queryParamsStringify: commaSeparatedQuerystring,
  credentials: isReactNative ? 'omit' : 'include',
  headers: {
    ...apiConfig.defaultHeaders,
    ...(isReactNative ? { 'x-device': 'mobile' } : {}),
  },
  accessToken: () => resolveAccessToken() ?? '',
  middleware: [
    jsonContentTypeMiddleware,
    languageMiddleware,
    authMiddleware,
    middleware,
  ],
});

export const authenticationApi = new AuthApi(sdkConfiguration);
export const notificationsApi = new NotificationsApi(sdkConfiguration);
export const otpApi = new OtpApi(sdkConfiguration);
export const waitlistApi = new WaitlistApi(sdkConfiguration);
export const accountDeletionRequestsApi = new AccountDeletionRequestsApi(sdkConfiguration);
export const onboardingApi = new OnboardingApi(sdkConfiguration);
export const playersApi = new PlayersApi(sdkConfiguration);
export const teamsApi = new TeamsApi(sdkConfiguration);
export const usersApi = new UsersApi(sdkConfiguration);
export const spoilerShieldApi = new SpoilerShieldApi(sdkConfiguration);
export const faqsApi = new FAQsApi(sdkConfiguration);
export const legalApi = new LegalApi(sdkConfiguration);
export const mediaApi = new MediaApi(sdkConfiguration);
export const referralsApi = new ReferralsApi(sdkConfiguration);
export const adminApi = new AdminApi(sdkConfiguration);
export const adminUsersApi = new AdminUsersApi(sdkConfiguration);
export const adminContentApi = new AdminContentApi(sdkConfiguration);
export const adminMonitoringApi = new AdminMonitoringApi(sdkConfiguration);
export const adminSubscriptionsApi = new AdminSubscriptionsApi(
  sdkConfiguration,
);
export const adminReferralsApi = new AdminReferralsApi(sdkConfiguration);
export const adminStreamingPlatformsApi = new AdminStreamingPlatformsApi(
  sdkConfiguration,
);
export const adminSportTopPlayersApi = new AdminSportTopPlayersApi(
  sdkConfiguration,
);
export const adminHighlightSourcesApi = new AdminHighlightSourcesApi(
  sdkConfiguration,
);
export const adminAlertsApi = new AdminAlertsApi(sdkConfiguration);
export const digestApi = new DigestApi(sdkConfiguration);
export const subscriptionsApi = new SubscriptionsApi(sdkConfiguration);
